import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "@mmm/contracts";
import { createSubject } from "../../api/commands.js";
import { getSubjectOverview } from "../../api/queries.js";
import { primeQuery } from "../../api/useQuery.js";
import { Modal } from "../shell/Modal.js";
import { Button, Input, useT } from "@mmm/ui";
/**
 * First-run gate. AppShell renders this outside the inert application shell,
 * so no route, sidebar control, or keyboard target can be used while it's up.
 *
 * It is no longer unconditionally blocking: "Explore by myself" is a real way
 * past it, recorded so it never asks that user again (see
 * preferences/firstSubjectChoice.ts). Escape and backdrop clicks are still
 * ignored — leaving is a deliberate choice, not an accident.
 */
interface FirstSubjectGateProps {
  onExplore: () => void;
  /**
   * Display name of the user this empty account belongs to — the ACTING
   * identity, not the signed-in one. Passed in rather than read from
   * `useAuth`, which always reports the real signed-in account and so greeted
   * the superadmin by name while they were impersonating someone else.
   */
  userName: string | undefined;
}

export function FirstSubjectGate({ onExplore, userName }: FirstSubjectGateProps) {
  const navigate = useNavigate();
  const t = useT();
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const subject = await createSubject({ title: trimmedTitle, color: PALETTE[0] });
      // The routed subject query can recover independently if this best-effort
      // cache warm-up fails.
      try {
        await primeQuery(getSubjectOverview, { subjectId: subject.id });
      } catch {
        // Best effort only.
      }
      navigate(`/subjects/${subject.id}/map`, { replace: true });
      // Keep the gate submitting until createSubject's cache invalidation
      // refreshes AppShell's subject list and removes the gate.
    } catch (err) {
      setError(err instanceof Error ? err.message : t("subjects.saveFailed"));
      setSubmitting(false);
    }
  };

  return (
    <Modal width={420} ariaLabel={t("maps.firstMapAction")}>
      <form className="mmm-first-subject-form" onSubmit={(event) => void handleSubmit(event)}>
        {/* First name only — the full display name can be long enough to wrap
            the greeting onto two lines, and a greeting reads better short. */}
        <p className="mmm-first-subject-greeting">
          {t("maps.firstMapGreeting", {
            name: firstNameOf(userName) ?? t("settings.accountFallbackName"),
          })}
        </p>
        <label className="mmm-first-subject-prompt" htmlFor="first-subject-title">
          {t("maps.firstMapPrompt")}
        </label>
        <Input
          id="first-subject-title"
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("maps.firstMapPlaceholder")}
        />
        <p className="mmm-first-subject-hint">{t("maps.firstMapHint")}</p>
        {error && <p role="alert" className="mmm-form-error">{error}</p>}
        <div className="mmm-first-subject-actions">
          <Button type="submit" variant="primary" disabled={!title.trim() || submitting}>
            {submitting ? t("maps.firstMapSaving") : t("maps.firstMapAction")}
          </Button>
          {/* Secondary, and never disabled while submitting is irrelevant to
              it — someone who decides to look around first shouldn't have to
              wait on a create they didn't start. */}
          <Button type="button" variant="secondary" onClick={onExplore}>
            {t("maps.exploreByMyself")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** "Jako Menkveld" -> "Jako"; undefined when there's no usable name. */
function firstNameOf(name: string | undefined): string | undefined {
  const first = name?.trim().split(/\s+/)[0];
  return first || undefined;
}
